import { Box, TextField } from '@mui/material';
import { ArrowDownIcon } from '~/assets/icons';
import { forwardRef, InputHTMLAttributes, memo, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './BaseDateTimePicker.module.scss';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';

const cx = classNames.bind(styles);

export type BaseDateTimePickerProps = {
  value?: Date | string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  onChange?: (date: Date | null) => void;
};

type TextFieldInputProps = InputHTMLAttributes<HTMLInputElement>;

export const BaseDateTimePicker = (props: BaseDateTimePickerProps) => {
  const { value, placeholder, error, helperText, onChange } = props;

  const [, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dateText, setDateText] = useState<string>('');

  useEffect(() => {
    if (value) {
      const date = dayjs(value).toDate();
      setSelectedDate(date);
      setDateText(dayjs(date).format('DD/MM/YYYY HH:mm'));
    }
  }, [value]);

  const handleChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date) setDateText(dayjs(date).format('DD/MM/YYYY HH:mm'));
    if (onChange) onChange(date);
    setIsOpen(false);
  };

  const TextFieldInput = forwardRef<HTMLInputElement, TextFieldInputProps>(
    ({ onClick }, ref) => (
      <TextField
        ref={ref}
        inputProps={{ readOnly: true }}
        InputProps={{
          endAdornment: <ArrowDownIcon className="w-[13px]" />,
        }}
        onClick={onClick}
        value={dateText}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
      />
    )
  );

  return (
    <Box className={cx('base-date-time-picker-container')}>
      <DatePicker
        selected={selectedDate}
        onChange={handleChange}
        showTimeSelect
        timeFormat="HH:mm"
        timeIntervals={15}
        dateFormat="dd/MM/yyyy HH:mm"
        customInput={
          <TextFieldInput
            value={dateText}
            onClick={() => {
              setIsOpen(true);
            }}
            readOnly
          />
        }
        onClickOutside={() => {
          setIsOpen(false);
        }}
        popperPlacement="top-start"
        showPopperArrow={false}
        calendarStartDay={1}
      />
    </Box>
  );
};

export default memo(BaseDateTimePicker);
